<script setup lang="ts">
import { just, nothing } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import { computed, nextTick, useTemplateRef, watch } from "vue";
import {
  initialImageAltDialogModel,
  updateImageAltDialogFn,
  type ImageAltDialogCmd,
} from "./ImageAltDialog.update";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";

const props = defineProps<{
  open: boolean;
  alt: string;
}>();

const emit = defineEmits<{
  close: [];
  apply: [alt: string];
}>();

const input = useTemplateRef<InstanceType<typeof ZwInput>>("input");

async function runCmd(cmd: ImageAltDialogCmd): Promise<void> {
  switch (cmd.kind) {
    case "focusInput":
      await nextTick();
      input.value?.focus();
      input.value?.select();
      return;
    case "apply":
      emit("apply", cmd.alt);
      return;
    default:
      assertNever(cmd);
  }
}

const { model, dispatch } = useTea(
  initialImageAltDialogModel,
  updateImageAltDialogFn,
  { runCmd: just(runCmd) },
);

const draft = computed({
  get: (): string => model.value.draft,
  set: (value: string): void => {
    void dispatch({ type: "DraftChanged", draft: value });
  },
});

watch(
  () => props.open,
  (open) => {
    if (open === false) return;
    void dispatch({ type: "Seed", alt: props.alt });
  },
);

function apply(): void {
  void dispatch({ type: "Apply" });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    apply();
  }
}
</script>

<template>
  <ZwDialog
    :open="open"
    title="Image description"
    :description="
      just('Shown to screen readers and when the image cannot load.')
    "
    size="md"
    :z-index="105"
    @close="emit('close')"
  >
    <ZwInput
      :id="nothing"
      ref="input"
      v-model="draft"
      placeholder="Describe this image…"
      aria-label="Image description"
      :label="nothing"
      @keydown="onKeydown"
    />

    <template #footer>
      <ZwButton @click="emit('close')">Cancel</ZwButton>
      <ZwButton variant="primary" @click="apply">Save</ZwButton>
    </template>
  </ZwDialog>
</template>
